import {
    BaseFeatureArgs,
    InitialQueryState,
    QueryManagerOptions,
    SkipOrArgs,
} from '@common/types/queryState';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { FacetFieldConfig } from '../types';

import { AnyFeatureHook } from '@common/hooks';
import {
    FacetingContextValue,
    FacetingPanel,
    FacetingProvider,
    NORMALIZATION_STRATEGIES,
    SearchResult,
    useCustomFieldDescriptions,
    useDynamicFacets,
    usePinnedFacets,
    useStickyFacets,
} from '@features/facetingEngine';

export interface FacetingLabels {
    title: string;
    getSectionLabel: (key: string) => string;
    getValueLabel: (key: string, val: string) => string;
}

export interface UseFacetedFeatureOptions<
    TData,
    TArgs extends BaseFeatureArgs,
> {
    queryHook: AnyFeatureHook<TData, TArgs>;
    initialArgs?: InitialQueryState;
    featureArgs?: SkipOrArgs<TArgs>;
    queryOptions?: QueryManagerOptions;
    baseConfig: FacetFieldConfig<TData>[];
    labels: FacetingLabels;
}

export const useFacetedFeature = <TData, TArgs extends BaseFeatureArgs>({
    queryHook,
    initialArgs,
    featureArgs,
    queryOptions,
    baseConfig,
    labels,
}: UseFacetedFeatureOptions<TData, TArgs>) => {
    const { data: cfdData } = useCustomFieldDescriptions();
    const config = useDynamicFacets<TData>(cfdData?.cfdArray, baseConfig);
    const {
        pinnedKeys: facetFields,
        pin,
        unpin,
        ...configs
    } = usePinnedFacets(
        config,
        baseConfig.map((c) => c.key),
    );
    const resolvedQueryOptions = useMemo(
        () => queryOptions || {},
        [queryOptions],
    );

    const mainQueryArgs = useMemo(() => {
        if (featureArgs === skipToken) return skipToken;
        return { ...(featureArgs || {}), facetFields } as TArgs;
    }, [featureArgs, facetFields]);

    const result = queryHook(initialArgs, mainQueryArgs, resolvedQueryOptions);

    const baselineResult = queryHook(
        { ...initialArgs, pageSize: 0 },
        mainQueryArgs,
        {
            ...resolvedQueryOptions,
            paramsOverride: {
                ...(resolvedQueryOptions.paramsOverride || {}),
                facets: {},
                ranges: {},
            },
        },
    );

    const sticky = useStickyFacets(
        result.facet || [],
        baselineResult.facet || [],
    );

    const contextValue: FacetingContextValue<TData> = useMemo(() => {
        const handleUnpin = (key: string) => {
            result.filterState.clearField(key);
            unpin(key);
        };

        const handlePin = (search: SearchResult) => {
            const keyToPin =
                search.type === 'field' ? search.key : search.facetKey!;
            pin(keyToPin);

            if (search.type === 'value' && search.facetKey) {
                result.filterState.toggleFacetFilter(search.facetKey, {
                    value: search.key,
                    operator: 'exact',
                });
            }
        };

        return {
            ...configs,
            facetData: result.facet || [],
            filterState: result.filterState,
            ...sticky,
            totalResults: baselineResult.itemCount,
            realTimeMatches: result.itemCount,
            getSectionLabel: labels.getSectionLabel,
            getValueLabel: labels.getValueLabel,
            getNormalizer: (key: string) =>
                NORMALIZATION_STRATEGIES[key] ?? ((v: string) => v),
            onPin: handlePin,
            onUnpin: handleUnpin,
        };
    }, [configs, result, baselineResult, sticky, pin, unpin, labels]);

    const facetNode = useMemo(
        () => (
            <FacetingProvider value={contextValue}>
                <FacetingPanel.Root>
                    <FacetingPanel.Header title={labels.title} />
                    <FacetingPanel.Content />
                </FacetingPanel.Root>
            </FacetingProvider>
        ),
        [contextValue, labels.title],
    );

    return {
        result,
        baselineResult,
        facetNode,
        contextValue,
    };
};
